import { SystemStyleObject } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'

export type { ColorScheme }

export type CardVariant = 'default' | 'selector'

const tokens = {
  dark: {
    bg: 'bg.card-elevated',
    borderColor: 'border.subtle',
    chevronColor: 'text.primary',
  },
  light: {
    bg: 'light.bg.card',
    borderColor: 'light.border.subtle',
    chevronColor: 'light.text.primary',
  },
} as const

export const cardStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  bg: tokens[colorScheme].bg,
  border: '1px solid',
  borderColor: tokens[colorScheme].borderColor,
  boxShadow: 'card',
  w: 'full',
})

export const cardHeaderStyles: SystemStyleObject = {
  display: 'flex',
  flexDirection: 'column',
  gapX: '16px',
  gapY: '6px',
  overflow: 'hidden',
  pb: '4px',
  pt: '20px',
  px: '20px',
}

export const cardFooterStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
  px: '20px',
  py: '16px',
  borderTop: '1px solid',
  borderTopColor: tokens[colorScheme].borderColor,
})

// ─── Collapsible row ──────────────────────────────────────────────────────────

export const collapsibleRowStyles: SystemStyleObject = {
  display: 'flex',
  alignItems: 'flex-start',
  cursor: 'pointer',
  justifyContent: 'space-between',
  mb: '10px',
}

export const chevronStyles = (
  isExpanded: boolean,
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  transition: 'transform 0.2s ease',
  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
  color: tokens[colorScheme].chevronColor,
})

// ─── Expandable content ───────────────────────────────────────────────────────

// CSS grid trick: animates grid-template-rows 0fr → 1fr without JS height
// measurement, running on the compositor thread.
export const expandableWrapperStyles = (
  isExpanded: boolean
): SystemStyleObject => ({
  display: 'grid',
  gridTemplateRows: isExpanded ? '1fr' : '0fr',
  transition: 'grid-template-rows 0.25s ease-in-out',
})

export const expandableInnerStyles: SystemStyleObject = {
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
}

// ─── Selector variant ─────────────────────────────────────────────────────────

const selectorTokens = {
  dark: {
    defaultBg: 'bg.card',
    defaultBorder: 'border.subtle',
    hoverBorder: 'border.input-focus',
    activeBg: 'bg.card-elevated',
    activeBorder: 'border.glow',
    labelColor: 'text.primary',
    descriptionColor: 'text.secondary',
  },
  light: {
    defaultBg: 'light.bg.card',
    defaultBorder: 'light.border.subtle',
    hoverBorder: 'light.border.input-focus',
    activeBg: 'light.bg.card-elevated',
    activeBorder: 'light.border.glow',
    labelColor: 'light.text.primary',
    descriptionColor: 'light.text.secondary',
  },
} as const

export const cardSelectorStyles = (
  isActive: boolean,
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  px: '16px',
  py: '24px',
  flex: '1 0 0',
  cursor: 'pointer',
  userSelect: 'none',
  border: '2px solid',
  transition: 'background 0.15s ease, border-color 0.15s ease',
  bg: isActive
    ? selectorTokens[colorScheme].activeBg
    : selectorTokens[colorScheme].defaultBg,
  borderColor: isActive
    ? selectorTokens[colorScheme].activeBorder
    : selectorTokens[colorScheme].defaultBorder,
  ...(!isActive && {
    _hover: {
      borderColor: selectorTokens[colorScheme].hoverBorder,
    },
  }),
})

export const cardSelectorLabelStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  fontFamily: "'Soehne Kraftig', sans-serif",
  fontStyle: 'normal',
  fontWeight: '500',
  fontSize: '14px',
  lineHeight: '20px',
  textAlign: 'center',
  color: selectorTokens[colorScheme].labelColor,
  margin: 0,
})

export const cardSelectorDescriptionStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  fontFamily: "'Soehne', sans-serif",
  fontStyle: 'normal',
  fontWeight: '400',
  fontSize: '12px',
  lineHeight: '16px',
  textAlign: 'center',
  color: selectorTokens[colorScheme].descriptionColor,
  margin: 0,
})
